/**
 * product.js
 * Handles:
 *  1. Variant selection (color swatches, size chips, select fallbacks)
 *     - Finds matching variant from product JSON
 *     - Dispatches `variant:change` on the section element
 *  2. Listeners on `variant:change`:
 *     - Updates hidden variant ID input
 *     - Syncs price display
 *     - Enables/disables ATC button
 *     - Replaces main gallery image when variant has its own featured_image
 *     - Updates URL via history.replaceState
 *  3. Quantity stepper (+/- buttons)
 *  4. Add-to-cart via /cart/add.js + dispatches `cart:updated` on document
 *  5. Sticky ATC bar (mobile, IntersectionObserver on main ATC button)
 *  6. Offer-card subtitles — recompute the [saving] / [discounted] /
 *     [remaining] money on variant:change so the figures track the price
 *  7. "Send as a gift" opt-in — chains a second /cart/add.js for the
 *     merchant-picked gift-wrap variant after the main add succeeds
 *
 * Task: K-057  Phase 4 final
 */

(function () {
  'use strict';

  /* ── Utility: find a variant matching the current option selections ─────── */

  function findMatchingVariant(product, selectedOptions) {
    return product.variants.find(function (variant) {
      return variant.options.every(function (opt, i) {
        return opt === selectedOptions[i];
      });
    }) || null;
  }

  /* ── Utility: money format ──────────────────────────────────────────────── */

  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.money_format) {
      var formatted = window.Shopify.money_format.replace(/\{\{.*?\}\}/, function () {
        return (cents / 100).toFixed(2);
      });
      return formatted;
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ── Variant picker initialisation ────────────────────────────────────────── */

  function initVariantPicker(section) {
    var picker = section.querySelector('[data-variant-picker]');
    if (!picker) return;

    var productJson = picker.dataset.productJson;
    if (!productJson) return;

    var product;
    try {
      product = JSON.parse(productJson);
    } catch (e) {
      return;
    }

    var sectionId = picker.dataset.sectionId;
    var variantInput = section.querySelector('input[name="id"]');
    if (!variantInput) return;

    /* Track currently selected option values keyed by position (1-indexed) */
    var selectedOptions = [];

    /* Populate initial selections from checked/selected inputs */
    picker.querySelectorAll('[data-option-position]').forEach(function (el) {
      var position = parseInt(el.dataset.optionPosition, 10) - 1;
      if (el.tagName === 'SELECT') {
        selectedOptions[position] = el.value;
      } else if (el.type === 'radio' && el.checked) {
        selectedOptions[position] = el.value;
      }
    });

    function handleOptionChange(optionPosition, newValue) {
      selectedOptions[optionPosition - 1] = newValue;

      var matched = findMatchingVariant(product, selectedOptions);

      /* Dispatch variant:change on the section for all listeners */
      section.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: matched, product: product }
      }));
    }

    /* Radio inputs (swatches + chips) */
    picker.querySelectorAll('input[type="radio"][data-option-position]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        var position = parseInt(this.dataset.optionPosition, 10);

        /* Update selected label for color option (capitalize for "Color: Owl") */
        var group = picker.querySelector('[data-option-group="' + (position - 1) + '"]');
        if (group) {
          var valueDisplay = group.querySelector('[data-option-selected-value]');
          if (valueDisplay) {
            var rawValue = this.value;
            var optionLabel = group.querySelector('.variant-picker__option-label');
            var isColorOpt = group.classList.contains('variant-picker__option--color');
            var displayValue = isColorOpt
              ? rawValue.charAt(0).toUpperCase() + rawValue.slice(1).toLowerCase()
              : rawValue;
            valueDisplay.textContent = displayValue;
            valueDisplay.dataset.optionSelectedValue = rawValue;
            void optionLabel; /* reserved for future label tweaks */
          }
          /* Toggle is-selected class on sibling labels */
          group.querySelectorAll('.variant-picker__swatch, .variant-picker__chip').forEach(function (lbl) {
            lbl.classList.remove('is-selected');
          });
          this.parentElement.classList.add('is-selected');
        }

        handleOptionChange(position, this.value);
      });
    });

    /* Select fallbacks */
    picker.querySelectorAll('select[data-option-position]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        var position = parseInt(this.dataset.optionPosition, 10);
        handleOptionChange(position, this.value);
      });
    });
  }

  /* ── variant:change listeners ──────────────────────────────────────────── */

  function initVariantListeners(section) {
    section.addEventListener('variant:change', function (e) {
      var variant = e.detail.variant;

      /* 1. Hidden variant ID input */
      var variantInput = section.querySelector('input[name="id"]');
      if (variantInput && variant) {
        variantInput.value = variant.id;
      }

      /* 2. Price update */
      syncPrice(section, variant);

      /* 3. ATC button state */
      syncAtcButton(section, variant);

      /* 4. Sticky ATC button state — sticky bar listens to variant:change
         itself (see initStickyAtc). No-op here. */

      /* 4b. Urgency message — re-evaluate per-variant stock */
      syncUrgency(section, variant);

      /* 5. Gallery image swap */
      if (variant && variant.featured_image) {
        swapGalleryImage(section, variant.featured_image);
      }

      /* 6. URL update */
      if (variant) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({ variantId: variant.id }, '', url.toString());
      }
    });
  }

  function syncPrice(section, variant) {
    var priceEl = section.querySelector('.product-price');
    if (!priceEl) return;

    if (!variant) {
      priceEl.querySelector('.product-price__current') && (priceEl.querySelector('.product-price__current').textContent = '—');
      return;
    }

    var currentEl = priceEl.querySelector('.product-price__current');
    var compareEl = priceEl.querySelector('.product-price__compare-at');
    var discountEl = priceEl.querySelector('.product-price__discount');

    if (currentEl) currentEl.textContent = formatMoney(variant.price);

    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    if (discountEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
        discountEl.textContent = '(' + pct + '% OFF)';
        discountEl.hidden = false;
      } else {
        discountEl.hidden = true;
      }
    }
  }

  function syncAtcButton(section, variant) {
    var atcBtn = section.querySelector('.product__atc');
    if (!atcBtn) return;

    var available = variant && variant.available;
    atcBtn.disabled = !available;

    var labelEl = atcBtn.querySelector('.button__label');
    if (labelEl) {
      if (!variant) {
        labelEl.textContent = atcBtn.dataset.soldOutText || 'Sold out';
      } else if (!available) {
        labelEl.textContent = atcBtn.dataset.soldOutText || 'Sold out';
      } else {
        labelEl.textContent = atcBtn.dataset.addToCartText || 'Add to bag';
      }
    }

    var stickyBtn = section.querySelector('.product-sticky-atc__btn');
    if (stickyBtn) {
      stickyBtn.disabled = !available;
    }
  }

  /* Re-evaluate urgency state for the currently-selected variant.
     Initial server render bakes the message based on `selected_or_first_available_variant`
     — but on variant switch we need to re-check `inventory_management` and
     `inventory_quantity` client-side so the message hides on untracked variants
     and updates between sold-out / low-stock / hidden states. */
  function syncUrgency(section, variant) {
    var urgencyBlock = section.querySelector('.product__block--urgency');
    if (!urgencyBlock || !variant) return;

    /* Threshold matches Liquid render in sections/product.liquid (urgency block) */
    var LOW_STOCK_THRESHOLD = 5;

    /* Shopify's `{{ product | json }}` filter strips inventory_quantity, so
       we read it from a parallel stock map emitted by variant-picker.liquid:
       data-variant-stock = { variantId: { mgmt, qty, available } }. */
    var picker = section.querySelector('[data-variant-stock]');
    var stockMap = {};
    if (picker) {
      try { stockMap = JSON.parse(picker.dataset.variantStock); } catch (e) {}
    }
    var stock = stockMap[variant.id] || {};
    var tracked = !!stock.mgmt;
    var qty = stock.qty;
    var available = stock.available !== undefined ? stock.available : variant.available;

    var soldOut = tracked && available === false;
    var lowStock = tracked && qty > 0 && qty <= LOW_STOCK_THRESHOLD;

    var soldOutText = urgencyBlock.dataset.soldOutText || 'This product is currently out of stock';
    var lowStockText = urgencyBlock.dataset.lowStockText || 'Only Few Left!';

    /* Remove any existing urgency paragraph; rebuild only when needed. */
    var existing = urgencyBlock.querySelector('.product__urgency');
    if (existing) existing.remove();

    if (!soldOut && !lowStock) return;

    var p = document.createElement('p');
    p.className = soldOut ? 'product__urgency product__urgency--sold-out' : 'product__urgency';
    var span = document.createElement('span');
    span.className = 'product__urgency-text';
    span.textContent = soldOut ? soldOutText : lowStockText;
    p.appendChild(span);
    urgencyBlock.appendChild(p);
  }

  function swapGalleryImage(section, featuredImage) {
    var mainImg = section.querySelector('.product-media__primary-img');
    if (!mainImg || !featuredImage) return;
    /* Use the raw CDN src — Shopify variant featured_image.src is already a
       full CDN URL. Clear srcset so browser doesn't serve a stale candidate. */
    mainImg.srcset = '';
    mainImg.src = featuredImage.src;
    if (featuredImage.alt) mainImg.alt = featuredImage.alt;
  }

  /* ── Quantity stepper ──────────────────────────────────────────────────── */

  function initQuantityStepper(section) {
    var input = section.querySelector('.product__qty-input');
    if (!input) return;

    section.querySelector('[data-qty-minus]') && section.querySelector('[data-qty-minus]').addEventListener('click', function () {
      var val = parseInt(input.value, 10) || 1;
      if (val > 1) input.value = val - 1;
    });

    section.querySelector('[data-qty-plus]') && section.querySelector('[data-qty-plus]').addEventListener('click', function () {
      var val = parseInt(input.value, 10) || 1;
      input.value = val + 1;
    });
  }

  /* ── "Send as a gift" opt-in ─────────────────────────────────────────────
     Wiring for the `gift_wrap` block in sections/product.liquid. The card
     itself is pure markup + CSS; everything transactional lives here so the
     block never opens a second cart code path.

     WHY IT HANGS OFF THE MAIN ATC AND NOT OFF ITS OWN BUTTON
     The main Add to Bag is a real <form> POST that this file already
     intercepts, and the sticky mobile bar drives that same form via
     requestSubmit(). Hooking the gift add into this one handler is therefore
     the only place that covers BOTH purchase paths without touching the
     add_to_cart / quantity block markup at all.

     WHY TWO SEQUENTIAL REQUESTS AND NOT ONE items:[] REQUEST
     summer-set adds its whole bundle in one multi-item /cart/add.js call,
     which is right there — the bundle is all-or-nothing. Here it is not: the
     product must reach the bag even if gift wrap fails (the gift variant can
     sell out between page render and click). /cart/add.js is atomic, so a
     combined request would fail the product too. So: main add first, exactly
     as before; gift add chained after it and its failure swallowed into an
     inline message. Neither request is fired concurrently with the other, so
     they cannot contend for the cart lock.

     The main line also carries a `Gift wrap` line-item property so the pairing
     is visible on the order. It is set on the JSON payload this handler
     already builds by hand, so the <form> markup is untouched.

     NO-JS: the block hides itself via <noscript> (see the Liquid comment) —
     a classic form POST cannot carry a second line item, so rather than
     degrade to a lying checkbox the card is simply absent. */

  function giftWrapErrorEl() {
    return document.querySelector('[data-gift-wrap-error]');
  }

  function showGiftWrapError(msg) {
    var el = giftWrapErrorEl();
    if (!el) return;
    if (msg) {
      /* Order matters for role="alert": the element has to be in the
         accessibility tree BEFORE the text lands, or the insertion that
         triggers the announcement happens inside a hidden subtree and some
         screen readers stay silent. Unhide first, then write. */
      el.hidden = false;
      el.textContent = msg;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  /* The ticked, usable gift checkbox, or null. */
  function activeGiftWrapInput() {
    var input = document.querySelector('[data-gift-wrap-input]');
    if (!input || !input.checked || input.disabled) return null;
    if (!input.dataset.giftVariant) return null;
    return input;
  }

  /* Adds the merchant's gift-wrap variant at quantity 1, ALWAYS 1, whatever
     the main product's quantity is. Never rejects: a gift failure must not
     break the chain that follows the main product's successful add. */
  function addGiftWrap(input) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: input.dataset.giftVariant, quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) {
          return res
            .json()
            .catch(function () { return {}; })
            .then(function (err) {
              throw new Error(err.description || err.message || '');
            });
        }
        /* Untick on success so a second Add to Bag does not silently buy a
           second gift wrap — same reset-after-add rule the summer-set block
           follows. The gift line is visible in the cart drawer that opens. */
        input.checked = false;
        return null;
      })
      .catch(function (err) {
        console.error('[product.js] gift-wrap add error:', (err && err.message) || err);
        showGiftWrapError((err && err.message) || input.dataset.giftErrorGeneric || '');
        return null;
      });
  }

  function initGiftWrap(section) {
    var input = section.querySelector('[data-gift-wrap-input]');
    if (!input) return;
    /* Clear a stale failure as soon as the shopper touches the control. */
    input.addEventListener('change', function () {
      showGiftWrapError('');
    });
  }

  /* ── Add-to-cart ─────────────────────────────────────────────────────────── */

  function initAtcForm(section) {
    var form = section.querySelector('.product__form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var atcBtn = form.querySelector('.product__atc');
      if (atcBtn && atcBtn.disabled) return;

      /* Read variant ID and quantity directly — avoids Shopify hidden fields
         (form_type, utf8) from being sent and potentially confusing the endpoint. */
      var variantInput = form.querySelector('input[name="id"]');
      var qtyInput = form.querySelector('[data-qty-input], [name="quantity"], .product__qty-input');
      var variantId = variantInput ? variantInput.value : '';
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      if (!variantId) {
        console.error('[product.js] ATC: no variant id found in form');
        return;
      }

      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.classList.add('is-loading');
      }

      /* Gift wrap is resolved BEFORE the request so the ticked state at click
         time is what gets charged, even if the shopper unticks mid-flight. */
      var giftInput = activeGiftWrapInput();
      var mainItem = { id: variantId, quantity: qty };
      if (giftInput) {
        var propName = giftInput.dataset.giftPropertyName;
        var propValue = giftInput.dataset.giftPropertyValue;
        if (propName && propValue) {
          mainItem.properties = {};
          mainItem.properties[propName] = propValue;
        }
        showGiftWrapError('');
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(mainItem)
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw new Error(err.description || 'Cart add failed (' + res.status + ')');
            });
          }
          return res.json();
        })
        .then(function () {
          /* Sequential, and only after the product itself is safely in the
             bag. addGiftWrap never rejects, so the /cart.js refresh and the
             cart:updated dispatch below still run when gift wrap fails. */
          return giftInput ? addGiftWrap(giftInput) : null;
        })
        .then(function () {
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          /* Standing rule: dispatch cart:updated so header badge refreshes,
             and cart:item-added so cart drawer auto-opens (K-061 contract). */
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: cart }));
        })
        .catch(function (err) {
          console.error('[product.js] cart/add error:', err.message || err);
        })
        .finally(function () {
          if (atcBtn) {
            atcBtn.disabled = false;
            atcBtn.classList.remove('is-loading');
          }
        });
    });
  }

  /* ── Sticky mobile Buy/Qty bar ──────────────────────────────────────────
     Always-visible bottom-fixed bar on mobile (CSS handles the breakpoint).
     The bar's qty <select> mirrors the inline qty so both stay in sync; the
     bar's button calls form.requestSubmit() on the main in-page product
     form so the existing ATC flow (cart/add.js, cart:updated dispatch) is
     reused without duplication. */

  function initStickyAtc(section) {
    var stickyBar = section.querySelector('[data-sticky-atc]');
    if (!stickyBar) return;

    var mainForm = section.querySelector('.product__form');
    var mainQty = section.querySelector('[data-qty-input]');
    var stickyQty = stickyBar.querySelector('[data-sticky-qty]');
    var stickyBtn = stickyBar.querySelector('[data-sticky-atc-submit]');

    /* Two-way sync between sticky qty and inline qty so the user can choose
       either control and the other stays in sync. */
    if (stickyQty && mainQty) {
      stickyQty.addEventListener('change', function () {
        mainQty.value = stickyQty.value;
      });
      mainQty.addEventListener('change', function () {
        if (stickyQty.value !== mainQty.value) {
          /* Only assign if a matching option exists; otherwise leave the
             sticky select untouched (e.g. inline allows higher max). */
          var opt = stickyQty.querySelector('option[value="' + mainQty.value + '"]');
          if (opt) stickyQty.value = mainQty.value;
        }
      });
    }

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        if (mainForm) mainForm.requestSubmit();
      });
    }

    /* Keep the sticky button label/state in sync with the inline ATC button. */
    section.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!stickyBtn) return;
      var available = variant && variant.available;
      stickyBtn.disabled = !available;
      var labelEl = stickyBtn.querySelector('.button__label');
      if (labelEl) {
        labelEl.textContent = available
          ? (stickyBtn.dataset.addToCartText || 'Add to bag')
          : (stickyBtn.dataset.soldOutText || 'Sold out');
      }
    });

    /* Smart-sticky: only show the bottom bar when the inline ATC button is
       OUT of view. Hide it when the user can see/tap the inline ATC — avoids
       a redundant overlay when both buttons would be visible at once. */
    var mainAtc = section.querySelector('.product__atc');
    if (mainAtc && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        stickyBar.classList.toggle('is-hidden', entry.isIntersecting);
      }, {
        /* Hide once 50% of the inline ATC is in view; smaller threshold
           would flicker at the edge of the viewport. */
        threshold: 0.5
      });
      io.observe(mainAtc);
      /* Initial state: hidden until first scroll callback fires. Prevents
         the bar from flashing in then sliding away on page load. */
      stickyBar.classList.add('is-hidden');
    }
  }

  /* ── Size guide trigger: hide when no size guide is linked ────────────── */

  /* ── Offer subtitles: keep the computed money true to the chosen variant ──

     snippets/product-offers.liquid renders these server-side from the
     selected variant's price. When the shopper switches variant the price
     moves, so the subtitle has to move with it or it silently lies.

     Only subtitles that actually carry a placeholder get the data-* payload,
     so an empty NodeList here means there is nothing to keep in sync.

     The arithmetic mirrors the Liquid exactly, including the rounding:
     Liquid's `price | times: pct | plus: 50 | divided_by: 100` is integer
     division, i.e. floor((p*pct + 50)/100), which is what Math.round does for
     non-negative values. Keep the two in step if either side changes. */

  /* Square brackets, not {{ curly }}: Shopify parses {{ ... }} inside a stored
     setting value as a dynamic-source binding and rejects the template on push
     ("Dynamic source 'remaining' is invalid."), so a curly placeholder can
     never be saved in a text setting. Keep these in step with the same three
     tokens in snippets/product-offers.liquid. */
  var OFFER_PLACEHOLDERS = {
    saving: '[saving]',
    discounted: '[discounted]',
    remaining: '[remaining]'
  };

  function substituteAll(text, token, value) {
    return text.split(token).join(value);
  }

  /* Returns the rendered subtitle, or null when the line should not be shown:
     a placeholder whose setting is missing, or a [remaining] line whose
     product has already reached the threshold. Same rule the Liquid applies, so
     server and client never disagree about visibility. null means hide, never
     remove — the threshold case reverses when a cheaper variant is picked. */
  function renderOfferSubtitle(el, priceCents) {
    var text = el.dataset.offerSubTemplate || '';
    var percent = parseInt(el.dataset.offerPercent, 10) || 0;
    var threshold = parseFloat(el.dataset.offerThreshold) || 0;

    var wantsSaving = text.indexOf(OFFER_PLACEHOLDERS.saving) !== -1;
    var wantsDiscounted = text.indexOf(OFFER_PLACEHOLDERS.discounted) !== -1;

    if (wantsSaving || wantsDiscounted) {
      if (percent <= 0) return null;
      var saving = Math.round(priceCents * percent / 100);
      text = substituteAll(text, OFFER_PLACEHOLDERS.saving, formatMoney(saving));
      text = substituteAll(text, OFFER_PLACEHOLDERS.discounted, formatMoney(priceCents - saving));
    }

    if (text.indexOf(OFFER_PLACEHOLDERS.remaining) !== -1) {
      if (threshold <= 0) return null;
      /* Never "add -$5 more to qualify" — a product already over the
         threshold has nothing left to add. */
      var remaining = Math.max(0, Math.round(threshold * 100) - priceCents);
      /* At or past the threshold there is nothing to add, so the line goes
         rather than reading "$0.00". Returning null hides it; this is
         reversible on purpose — a switch back to a cheaper variant makes
         remaining positive again and the caller un-hides the same node. */
      if (remaining === 0) return null;
      text = substituteAll(text, OFFER_PLACEHOLDERS.remaining, formatMoney(remaining));
    }

    return text;
  }

  function initOfferSubtitles(section) {
    var subs = section.querySelectorAll('.offers-card__sub[data-offer-sub-template]');
    if (!subs.length) return;

    section.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant || typeof variant.price !== 'number') return;

      subs.forEach(function (el) {
        var text = renderOfferSubtitle(el, variant.price);
        if (text === null) {
          el.hidden = true;
          return;
        }
        el.hidden = false;
        /* textContent, not innerHTML: the template comes back out of the
           dataset already entity-decoded, so assigning it as markup would
           re-interpret merchant copy. */
        el.textContent = text;
      });
    });
  }

  function initSizeGuideTrigger(section) {
    var noGuide = section.querySelector('[data-no-size-guide]');
    if (!noGuide) return;

    var trigger = section.querySelector('[data-modal-open="size-guide"]');
    if (trigger) {
      var block = trigger.closest('.product__block--size-guide');
      if (block) block.hidden = true;
    }
  }

  /* ── #reviews hash → open the accordion ───────────────────────────────────
     The product-rating count link points at #reviews (a <details> element).
     Default browser behaviour is to scroll-into-view but leave the accordion
     closed. Open it programmatically on hash match (both on initial load and
     on subsequent in-page hash changes). */
  function openReviewsIfHashed() {
    if (window.location.hash !== '#reviews') return;
    var d = document.getElementById('reviews');
    if (d && d.tagName === 'DETAILS') d.open = true;
  }

  document.addEventListener('DOMContentLoaded', openReviewsIfHashed);
  window.addEventListener('hashchange', openReviewsIfHashed);

  /* ── Bootstrap — one section at a time ────────────────────────────────── */

  function initProductSection(section) {
    initVariantPicker(section);
    initVariantListeners(section);
    initQuantityStepper(section);
    initGiftWrap(section);
    initAtcForm(section);
    initStickyAtc(section);
    initSizeGuideTrigger(section);
    initOfferSubtitles(section);
  }

  document.querySelectorAll('[id^="product-"]').forEach(function (section) {
    if (section.hasAttribute('aria-label')) {
      initProductSection(section);
    }
  });

  /* ── Same-page jumps ─────────────────────────────────────────────────────────
     Two in-page jumps share one delegated listener:

       [data-scroll-to-set]     offers card  -> "Build the summer set" block
       [data-scroll-to-reviews] rating cluster -> the #reviews accordion

     Clicking a tagged element scrolls to its target on the SAME page. No
     navigation, no hash written to the URL — a hash would add a history entry
     and re-jump on reload, and it would also fight the browser's own
     restore-scroll behaviour.

     Delegated from document so it survives theme-editor section re-renders,
     which replace the offers and rating markup wholesale.

     Degrades to nothing when the target is absent (the summer-set block renders
     nothing until a collection is picked; #reviews only exists on the PDP),
     rather than throwing or scrolling to the top. Both triggers keep a real
     href/button, so with JS off the browser's own anchor jump still lands.

     Reduced motion: scrollIntoView({behavior:'smooth'}) overrides CSS
     scroll-behavior, so critical.css's `scroll-behavior: auto !important` does
     NOT cover this path — the preference has to be read in JS. */
  var SCROLL_JUMPS = [
    { attr: 'data-scroll-to-set', target: '.product__summer-set' },
    { attr: 'data-scroll-to-reviews', target: '#reviews' }
  ];

  /* The reviews accordion is a <details> with no `open` attribute, so a plain
     scroll would leave the shopper staring at a collapsed "REVIEWS" summary and
     read as broken. Expand it first, THEN scroll — order matters, because
     opening it changes the document height under us.

     Inert for .product__summer-set, which is a <div>. */
  function scrollToJumpTarget(target, smooth) {
    if (target.tagName === 'DETAILS') target.open = true;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      behavior: smooth && !reduced ? 'smooth' : 'auto',
      block: 'start'
    });
  }

  document.addEventListener('click', function (event) {
    /* event.target can be a non-Element (e.g. a text node in some synthetic
       events), and closest() only exists on Element. */
    if (!event.target || typeof event.target.closest !== 'function') return;

    for (var i = 0; i < SCROLL_JUMPS.length; i++) {
      var jump = SCROLL_JUMPS[i];
      if (!event.target.closest('[' + jump.attr + ']')) continue;

      var target = document.querySelector(jump.target);
      if (!target) return;

      event.preventDefault();
      scrollToJumpTarget(target, true);
      return;
    }
  });

  /* Direct load with #reviews in the URL — someone pastes or bookmarks the
     link the rating cluster points at. The browser jumps to the collapsed
     <details> on its own, so without this the pasted link is exactly the
     broken-looking landing the click handler avoids. Re-scroll after opening
     because expanding it shifts the anchor.

     Never smooth: an animated scroll on first paint is disorienting and fights
     the browser's own jump. */
  function openReviewsFromHash() {
    if (window.location.hash !== '#reviews') return;

    var target = document.getElementById('reviews');
    if (!target) return;

    scrollToJumpTarget(target, false);
  }

  /* product.js is deferred, so this normally runs before DOMContentLoaded — the
     readyState branch is for the case where it is ever loaded async/late. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openReviewsFromHash);
  } else {
    openReviewsFromHash();
  }

})();
